import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clientService from '../../../services/clientService';
import { FolderKanban, Plus, Search, Filter, User, Calendar, Briefcase, TrendingUp, Edit, Trash2, Eye } from 'lucide-react';

const ProjectsList = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [type, setType] = useState('all');
  const [clientId, setClientId] = useState('all');
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [status, type, clientId]);

  const fetchClients = async () => {
    try {
      const res = await clientService.getClients({ per_page: 100 });
      setClients(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const params = {};
      if (status !== 'all') params.status = status;
      if (type !== 'all') params.project_type = type;
      if (clientId !== 'all') params.client_id = clientId;
      if (search) params.search = search;
      const res = await clientService.getProjects(params);
      setProjects(res.data || []);
    } catch (e) {
      console.error('Error loading projects', e);
      alert('Error loading projects');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await clientService.deleteProject(id);
      fetchProjects();
    } catch (e) {
      alert('Error deleting project');
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-primary mb-2">Project Management</h1>
        <p className="text-gray-600">Track and manage all client projects</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              value={search}
              onChange={(e)=>setSearch(e.target.value)}
              onKeyDown={(e)=> e.key==='Enter' && fetchProjects()}
              placeholder="Search projects..."
              className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary"
            />
          </div>
          <select value={status} onChange={(e)=>setStatus(e.target.value)} className="px-4 py-2 border-2 rounded-lg">
            {['all','pending','in_progress','completed','on_hold','cancelled'].map(s=> (
              <option key={s} value={s}>{s.replace('_',' ')}</option>
            ))}
          </select>
          <select value={type} onChange={(e)=>setType(e.target.value)} className="px-4 py-2 border-2 rounded-lg">
            {['all','website','mobile_app','digital_marketing','seo','ecommerce','custom_software','amc','other'].map(t=> (
              <option key={t} value={t}>{t.replace('_',' ')}</option>
            ))}
          </select>
          <select value={clientId} onChange={(e)=>setClientId(e.target.value)} className="px-4 py-2 border-2 rounded-lg">
            <option value="all">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        <div className="mt-4 flex justify-between">
          <button onClick={fetchProjects} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg flex items-center gap-2"><Filter className="w-4 h-4"/>Apply Filters</button>
          <Link to="/admin/projects/new" className="bg-primary hover:bg-primary-700 text-white px-6 py-2 rounded-lg inline-flex items-center gap-2"><Plus className="w-5 h-5"/>Add Project</Link>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-primary to-primary-700">
          <h2 className="text-xl font-bold text-white">All Projects ({projects.length})</h2>
        </div>
        {loading ? (
          <div className="p-10 text-center">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Budget / Paid</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {projects.length === 0 ? (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No projects found</td></tr>
                ) : (
                  projects.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">{p.project_name}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-2"><Calendar className="w-3 h-3"/> {p.start_date || '-'} → {p.end_date || '-'}</div>
                      </td>
                      <td className="px-6 py-4"><div className="flex items-center gap-2"><User className="w-4 h-4"/>{p.client?.company_name || p.client?.contact_person || '—'}</div></td>
                      <td className="px-6 py-4 capitalize">{p.project_type?.replace('_',' ')}</td>
                      <td className="px-6 py-4">฿{(p.budget||0).toLocaleString()} / <span className="text-green-600">฿{(p.paid_amount||0).toLocaleString()}</span></td>
                      <td className="px-6 py-4">
                        <div className="w-32">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="h-2 bg-primary rounded-full" style={{ width: `${p.progress||0}%` }} />
                          </div>
                          <div className="text-xs text-gray-600 mt-1">{p.progress || 0}%</div>
                        </div>
                      </td>
                      <td className="px-6 py-4"><span className={`px-3 py-1 rounded-full text-xs font-semibold ${p.status==='completed'?'bg-green-100 text-green-800':p.status==='in_progress'?'bg-blue-100 text-blue-800':p.status==='on_hold'?'bg-orange-100 text-orange-800':'bg-gray-100 text-gray-800'}`}>{p.status?.replace('_',' ')}</span></td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/admin/projects/edit/${p.id}`} className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded" title="Edit"><Edit className="w-5 h-5"/></Link>
                          <button onClick={()=>handleDelete(p.id)} className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded" title="Delete"><Trash2 className="w-5 h-5"/></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsList;
